// src/pages/Warehouses/AddCategory.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../../axios';
import {
  Container, Paper, Typography, Grid, TextField, FormControl, InputLabel,
  Select, MenuItem, FormControlLabel, Switch, Button, Box, Divider, 
  Skeleton, Chip, Tooltip, IconButton, useTheme, useMediaQuery, Alert, Fade
} from '@mui/material';
import {
  Delete as DeleteIcon, Edit as EditIcon, Save as SaveIcon, Category as CategoryIcon,
  Print as PrintIcon, Store as StoreIcon, CheckCircle as CheckCircleIcon, Cancel as CancelIcon
} from '@mui/icons-material';

export default function AddCategory() {
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const companyId = parseInt(localStorage.getItem('companyId'), 10);

  const [formData, setFormData] = useState({
    nameAr: '', nameEn: '', printerName: '', branchId: 0,
    allBranches: false, isActive: true, showInPos: true,
  });
  
  const [branches, setBranches] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!companyId) return;
    Promise.all([
      axios.get(`/Branch/by-company/${companyId}`),
      axios.get(`/Category/printers`),
      axios.get(`/Category/by-company/${companyId}`)
    ])
      .then(([b, p, c]) => {
        setBranches(b.data);
        setPrinters(p.data);
        setCategories(c.data);
        if (!id && b.data.length) setFormData(f => ({ ...f, branchId: b.data[0].id }));
      })
      .finally(() => setLoadingData(false));
  }, [companyId, id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoadingSubmit(true);
    try {
      const payload = { ...formData, companyId, branchId: formData.allBranches ? 0 : formData.branchId };
      id ? await axios.put(`/Category/${id}`, payload) : await axios.post(`/Category`, payload);
      
      setSuccessMessage(id ? '✅ تم تحديث التصنيف بنجاح!' : '✅ تم إضافة التصنيف بنجاح!');
      if (!id) setFormData({ nameAr: '', nameEn: '', printerName: '', branchId: branches[0]?.id || 0, allBranches: false, isActive: true, showInPos: true });
      
      const res = await axios.get(`/Category/by-company/${companyId}`);
      setCategories(res.data);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setErrorMessage('❌ حدث خطأ أثناء الحفظ');
    } finally {
      setLoadingSubmit(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6, px: { xs: 2, md: 4 } }} dir="rtl">
      
{/* Header - ترويسة مختصرة تعتمد على الثيم */}
<Box
  sx={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1.5,
    px: { xs: 1, sm: 2 },
    py: 1.5,
    mb: 2,
    borderBottom: 1,
    borderColor: 'divider',
    color: 'text.primary',
  }}
>
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.2,
      minWidth: 0,
    }}
  >
    <CategoryIcon
      sx={{
        color: 'primary.main',
        fontSize: 26,
      }}
    />

    <Typography
      variant="h6"
      sx={{
        fontWeight: 800,
        color: 'text.primary',
        fontSize: { xs: '1rem', sm: '1.2rem' },
      }}
    >
      إدارة التصنيفات
    </Typography>
  </Box>

  <Typography
    variant="caption"
    sx={{
      color: 'text.secondary',
      whiteSpace: 'nowrap',
      fontSize: '0.8rem',
    }}
  >
    {id ? 'تعديل التصنيف' : 'إضافة تصنيف'}
  </Typography>
</Box>


      {/* Form Section */}
      <Paper elevation={0} sx={{ p: 4, mb: 4, borderRadius: '24px', border: '1px solid #e2e8f0' }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 800 }}>{id ? 'تعديل التصنيف' : 'إضافة تصنيف جديد'}</Typography>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="الاسم العربي" name="nameAr" value={formData.nameAr} onChange={e => setFormData({...formData, nameAr: e.target.value})} required sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px' } }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="الاسم الإنجليزي" name="nameEn" value={formData.nameEn} onChange={e => setFormData({...formData, nameEn: e.target.value})} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px' } }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>الطابعة</InputLabel>
                <Select value={formData.printerName} onChange={e => setFormData({...formData, printerName: e.target.value})} sx={{ borderRadius: '16px' }}>
                  <MenuItem value="">بدون طابعة</MenuItem>
                  {printers.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth disabled={formData.allBranches}>
                <InputLabel>الفرع</InputLabel>
                <Select value={formData.branchId} onChange={e => setFormData({...formData, branchId: e.target.value})} sx={{ borderRadius: '16px' }}>
                  {branches.map(b => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControlLabel control={<Switch checked={formData.allBranches} onChange={e => setFormData({...formData, allBranches: e.target.checked})} />} label="كل الفروع" />
              <FormControlLabel control={<Switch checked={formData.isActive} onChange={e => setFormData({...formData, isActive: e.target.checked})} />} label="نشط" />
            </Grid>
            <Grid item xs={12}>
              <Button type="submit" variant="contained" size="large" disabled={loadingSubmit} sx={{ borderRadius: '16px', px: 5, py: 1.5, background: '#1e293b' }}>
                {loadingSubmit ? 'جاري الحفظ...' : '💾 حفظ التصنيف'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Table Section */}
      <Paper elevation={0} sx={{ p: 4, borderRadius: '24px', border: '1px solid #e2e8f0' }}>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 800 }}>قائمة التصنيفات</Typography>
        {loadingData ? <Skeleton height={200} /> : (
          <Box sx={{ overflowX: 'auto' }}>
            <table className="w-full text-right border-collapse">
              <thead>
                <tr className="border-b border-slate-200 text-slate-500">
                  <th className="py-3 px-4">#</th>
                  <th className="py-3 px-4">الاسم</th>
                  <th className="py-3 px-4">الطابعة</th>
                  <th className="py-3 px-4">الفرع</th>
                  <th className="py-3 px-4 text-center">إجراء</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((c, i) => (
                  <tr key={c.id} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-4 px-4 font-bold">{i + 1}</td>
                    <td className="py-4 px-4 font-semibold">{c.nameAr}</td>
                    <td className="py-4 px-4"><Chip label={c.printerName || 'لا يوجد'} size="small" /></td>
                    <td className="py-4 px-4">{c.allBranches ? 'كل الفروع' : c.branchName}</td>
                    <td className="py-4 px-4 text-center">
                      <IconButton color="primary" onClick={() => navigate(`/edit-category/${c.id}`)}><EditIcon /></IconButton>
                      <IconButton color="error" onClick={() => {/* Handle delete */}}><DeleteIcon /></IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </Paper>
    </Container>
  );
}